import React, { useMemo, useState } from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, AlertTriangle, CheckCircle2, MessageCircle, ShieldAlert, ArrowRightLeft } from 'lucide-react';
import { playSuccess, playWrong } from '../../utils/sounds';
import { Celebration } from '../Celebration';
import { shuffle } from '../../utils/shuffle';
interface Objection {
  signal: string;
  reframe: string;
}
const OBJECTIONS: Record<string, Objection[]> = {
  Nephrologist: [{
    signal: '"I need to see more long-term safety data before adding another agent."',
    reframe: 'Acknowledge the concern, then pivot to the cost of inaction — every month of delay accelerates irreversible kidney damage. Frame early addition as risk reduction, not risk addition.'
  }, {
    signal: '"My patients are too fragile for aggressive multi-modal treatment."',
    reframe: "Reframe: it's not about being aggressive — it's about being proactive. Position early treatment as protecting fragile patients from reaching the point of no return."
  }, {
    signal: '"I\'ll wait until the guidelines are clearer on sequencing."',
    reframe: 'Share emerging consensus and real-world evidence. Highlight that waiting for perfect guidelines means patients progress while evidence already supports action.'
  }],
  Cardiologist: [{
    signal: '"Show me the CV outcome data — I need hard endpoints."',
    reframe: 'Lead with CV outcome evidence and dual organ protection data. Cardiologists respond to numbers — make the case with NNT and absolute risk reduction.'
  }, {
    signal: "\"I don't have time to manage the kidney side — that's the nephrologist's job.\"",
    reframe: 'Position kidney protection as inseparable from CV protection. Frame it as one treatment decision, not an additional workload.'
  }],
  Endocrinologist: [{
    signal: '"I\'m already managing their metabolic risk with SGLT2 and GLP1."',
    reframe: "Acknowledge their proactive metabolic management, then show the residual risk that remains. Position the next step as completing the protection, not replacing what they're doing."
  }, {
    signal: '"Non-glycaemic treatment isn\'t really my area — I\'d refer for that."',
    reframe: 'Challenge the boundary gently: their ongoing relationship with the patient makes them ideally placed. Frame it as extending their preventative approach, not stepping outside their lane.'
  }, {
    signal: '"I need to see the biomarker data before I escalate."',
    reframe: 'Lean into their data-driven nature. Provide clear biomarker thresholds and monitoring protocols that give them confidence to act.'
  }],
  PCP: [{
    signal: '"I\'m not comfortable escalating beyond first-line therapy."',
    reframe: 'Provide simple, step-by-step escalation pathways. Show them that peers are doing this successfully — social proof and guidelines reduce the confidence gap.'
  }, {
    signal: '"I\'ll refer to the specialist when it gets more serious."',
    reframe: 'Highlight that "more serious" often means irreversible damage has already occurred. Frame early action as preventing the need for specialist rescue.'
  }, {
    signal: '"My patients have so many comorbidities — I can\'t add another medication."',
    reframe: 'Reframe from "adding a medication" to "simplifying the future." Early treatment reduces downstream complexity, hospitalisations, and polypharmacy.'
  }]
};
export function TreatmentBehavior({
  state,
  updateState,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const [showFeedback, setShowFeedback] = useState(!!state.predictedBehavior);
  const [showObjections, setShowObjections] = useState(false);
  const [celebration, setCelebration] = useState<'success' | 'wrong' | null>(null);
  const options = {
    Nephrologist: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early and aggressively',
      isCorrect: false
    }, {
      id: 'Mid',
      text: 'They focus on foundational therapies and escalate cautiously',
      isCorrect: false
    }, {
      id: 'Late',
      text: 'They reserve new treatments for late-stage progression',
      isCorrect: true
    }],
    Cardiologist: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early and aggressively',
      isCorrect: true
    }, {
      id: 'Mid',
      text: 'They focus on foundational therapies and escalate cautiously',
      isCorrect: false
    }, {
      id: 'Late',
      text: 'They reserve new treatments for late-stage progression',
      isCorrect: false
    }],
    Endocrinologist: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early and aggressively',
      isCorrect: false
    }, {
      id: 'Mid',
      text: 'They escalate metabolically but refer out for non-glycaemic treatment',
      isCorrect: true
    }, {
      id: 'Late',
      text: 'They reserve new treatments for late-stage progression',
      isCorrect: false
    }],
    PCP: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early based on clinical evidence',
      isCorrect: false
    }, {
      id: 'Mid',
      text: 'They follow guidelines and peer behavior — sticking to tried-and-true therapies until peers or protocols say otherwise',
      isCorrect: true
    }, {
      id: 'Late',
      text: 'They independently escalate treatment based on biomarker trends',
      isCorrect: false
    }]
  };
  const currentOptions = useMemo(() => shuffle(options[role]), [role]);
  const objections = OBJECTIONS[role];
  const handleSelect = (id: string) => {
    if (showFeedback) return;
    updateState({
      predictedBehavior: id
    });
    const isCorrect = currentOptions.find(o => o.id === id)?.isCorrect || false;
    setTimeout(() => {
      setShowFeedback(true);
      if (isCorrect) {
        playSuccess();
        setCelebration('success');
      } else {
        playWrong();
        setCelebration('wrong');
      }
      setTimeout(() => setCelebration(null), 2000);
    }, 500);
  };
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-756">
      <Celebration type={celebration} color={color} data-id="element-757" />
      <div className="max-w-3xl w-full mx-auto" data-id="element-758">
        <div className="text-center mb-10" data-id="element-759">
          <span className="text-sm font-bold uppercase tracking-widest mb-2 block" style={{
          color
        }} data-id="element-760">
            Behavior Prediction
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-dark" data-id="element-761">
            How do {role}s typically approach treatment?
          </h2>
          <p className="text-gray-500 mt-2" data-id="element-762">
            As a group, select the statement that best describes their reality.
          </p>
        </div>

        <div className="space-y-4 mb-10" data-id="element-763">
          {currentOptions.map(opt => {
          const isSelected = state.predictedBehavior === opt.id;
          const isFaded = showFeedback && !opt.isCorrect && !isSelected;
          const showCorrect = showFeedback && opt.isCorrect;
          const showIncorrect = showFeedback && isSelected && !opt.isCorrect;
          return <motion.button key={opt.id} whileHover={!showFeedback ? {
            scale: 1.01
          } : {}} whileTap={!showFeedback ? {
            scale: 0.99
          } : {}} onClick={() => handleSelect(opt.id)} className={`
                  w-full p-6 rounded-2xl border-2 text-left flex items-center justify-between transition-all duration-300
                  ${isSelected && !showFeedback ? 'bg-gray-50' : 'bg-white'}
                  ${showCorrect ? 'bg-green-50 border-green-500' : ''}
                  ${showIncorrect ? 'bg-red-50 border-red-300' : ''}
                  ${!showCorrect && !showIncorrect && !isSelected ? 'border-gray-200 hover:border-gray-300' : ''}
                  ${isFaded ? 'opacity-40 grayscale' : 'opacity-100'}
                `} style={{
            borderColor: isSelected && !showFeedback ? color : undefined
          }} data-id="element-764">
                <span className={`text-lg font-medium ${showCorrect ? 'text-green-800' : showIncorrect ? 'text-red-800' : 'text-dark'}`} data-id="element-765">
                  {opt.text}
                </span>
                {showCorrect && <CheckCircle2 className="text-green-500 flex-shrink-0 ml-4" size={24} data-id="element-766" />}
                {!showFeedback && <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ml-4 ${isSelected ? 'border-transparent' : 'border-gray-300'}`} style={{
              backgroundColor: isSelected ? color : 'transparent'
            }} data-id="element-767">
                    {isSelected && <div className="w-2 h-2 bg-white rounded-full" data-id="element-768" />}
                  </div>}
              </motion.button>;
        })}
        </div>

        <AnimatePresence data-id="element-769">
          {showFeedback && <motion.div initial={{
          opacity: 0,
          height: 0,
          y: 20
        }} animate={{
          opacity: 1,
          height: 'auto',
          y: 0
        }} className="overflow-hidden" data-id="element-770">
              {/* Timing Insight */}
              <div className="bg-gray-50 border border-gray-200 rounded-3xl p-8 mb-8" data-id="element-771">
                <h3 className="text-xl font-bold mb-3" style={{
              color
            }} data-id="element-772">
                  Timing Insight
                </h3>
                <p className="text-gray-700 leading-relaxed" data-id="element-773">
                  {role === 'Nephrologist' && 'Nephrologists often receive patients late in the disease progression. Combined with safety concerns, this leads to a more reactive, cautious approach rather than early multi-modal initiation.'}
                  {role === 'Cardiologist' && 'Cardiologists are highly outcome-driven and comfortable with rapid escalation. They are more likely to initiate multi-modal therapies early to prevent downstream events.'}
                  {role === 'Endocrinologist' && 'Endocrinologists are proactive with metabolic control (SGLT2/GLP1) but often hesitate to escalate to non-glycaemic treatments, preferring to refer out.'}
                  {role === 'PCP' && 'PCPs are heavily guideline- and peer-driven. They stick with tried-and-true therapies and look to what colleagues are doing before escalating. Without clear guideline endorsement or visible peer adoption, they default to conservative management and early referral.'}
                </p>
              </div>

              {/* Cost of Delay */}
              <div className="bg-red-50 border border-red-100 rounded-3xl p-8 mb-8 relative overflow-hidden" data-id="element-774">
                <div className="absolute top-0 right-0 p-8 opacity-10 text-red-500 pointer-events-none" data-id="element-775">
                  <AlertTriangle size={120} data-id="element-776" />
                </div>
                <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center" data-id="element-777">
                  <AlertTriangle className="mr-2" size={24} data-id="element-778" />
                  The Cost of Delayed Treatment
                </h3>
                <ul className="space-y-3 relative z-10" data-id="element-779">
                  <li className="flex items-start text-red-900" data-id="element-780">
                    <span className="mr-3 mt-1.5 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" data-id="element-781"></span>
                    <span data-id="element-782">
                      <strong data-id="element-783">Faster CKD progression:</strong> Irreversible loss
                      of kidney function.
                    </span>
                  </li>
                  <li className="flex items-start text-red-900" data-id="element-784">
                    <span className="mr-3 mt-1.5 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" data-id="element-785"></span>
                    <span data-id="element-786">
                      <strong data-id="element-787">Increased CV risk:</strong> Patients face up to 3x
                      higher cardiovascular risk.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Objection Handling Section */}
              <div className="mb-8" data-id="element-788">
                <button onClick={() => setShowObjections(!showObjections)} className="w-full bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center justify-between hover:bg-amber-100/70 transition-colors" data-id="element-789">
                  <div className="flex items-center" data-id="element-790">
                    <ShieldAlert className="text-amber-600 mr-4 flex-shrink-0" size={24} data-id="element-791" />
                    <div className="text-left" data-id="element-792">
                      <h4 className="text-amber-900 font-bold" data-id="element-793">
                        Signals & Objections You'll Hear
                      </h4>
                      <p className="text-amber-700 text-sm mt-0.5" data-id="element-794">
                        {objections.length} common objections from {role}s —
                        with suggested reframes
                      </p>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: showObjections ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-795">
                    <ArrowRightLeft className="text-amber-500" size={20} data-id="element-796" />
                  </motion.div>
                </button>

                <AnimatePresence data-id="element-797">
                  {showObjections && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-798">
                      <div className="space-y-4 mt-4" data-id="element-799">
                        {objections.map((obj, i) => <motion.div key={i} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: i * 0.15
                  }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-800">
                            {/* The objection/signal */}
                            <div className="p-5 bg-amber-50/50 border-b border-amber-100" data-id="element-801">
                              <div className="flex items-start" data-id="element-802">
                                <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 text-xs font-bold" data-id="element-803">
                                  {i + 1}
                                </div>
                                <div data-id="element-804">
                                  <span className="text-xs font-bold uppercase tracking-wider text-amber-600 block mb-1" data-id="element-805">
                                    You'll hear
                                  </span>
                                  <p className="text-amber-900 font-semibold text-lg leading-snug" data-id="element-806">
                                    {obj.signal}
                                  </p>
                                </div>
                              </div>
                            </div>
                            {/* The reframe */}
                            <div className="p-5" data-id="element-807">
                              <div className="flex items-start" data-id="element-808">
                                <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0" data-id="element-809">
                                  <ArrowRight size={12} data-id="element-810" />
                                </div>
                                <div data-id="element-811">
                                  <span className="text-xs font-bold uppercase tracking-wider text-green-600 block mb-1" data-id="element-812">
                                    Reframe
                                  </span>
                                  <p className="text-gray-700 leading-relaxed" data-id="element-813">
                                    {obj.reframe}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </motion.div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Discussion Prompt */}
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-8 flex items-start" data-id="element-814">
                <MessageCircle className="text-blue-500 mr-4 mt-1 flex-shrink-0" size={24} data-id="element-815" />
                <div data-id="element-816">
                  <h4 className="text-blue-900 font-bold mb-2" data-id="element-817">
                    Discuss with your group:
                  </h4>
                  <ul className="text-blue-800 space-y-1.5" data-id="element-818">
                    <li data-id="element-819">
                      <strong data-id="element-820">Targeting:</strong> Which {role}s are closest to
                      changing this behavior?
                    </li>
                    <li data-id="element-821">
                      <strong data-id="element-822">Arguments:</strong> Which reframe above would be
                      most effective — and why?
                    </li>
                    <li data-id="element-823">
                      <strong data-id="element-824">Motivation:</strong> What's the emotional driver
                      that could overcome their objections?
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-center pb-8" data-id="element-825">
                <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-826">
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-827" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}